import { countHiddenMeters, countRulebookRules, HttpStatusCode, MeterType, toRulebookPublicView, } from "@next-platform/packages-ts-types";
import { axiosClient, getBaseUrlVersioned } from "../../services/axiosClient";
const rulebooksUrl = (path = "") => `${getBaseUrlVersioned(null)}/rulebooks${path}`;
/**
 * Owner previews reveal every level — the creator wrote them. Reveal maps can't express this
 * (dice reveal only the rolled face), so reveal each projected level and restore its
 * label/caption from the definition by index — unrevealed levels carry bounds only.
 */
const revealAllLevels = (definitionLevels, viewLevels) => {
    if (viewLevels === undefined) {
        return undefined;
    }
    return viewLevels.map((level, index) => {
        const definitionLevel = definitionLevels?.[index];
        return {
            ...level,
            ...(definitionLevel?.label !== undefined ? { label: definitionLevel.label } : {}),
            ...(definitionLevel?.caption !== undefined ? { caption: definitionLevel.caption } : {}),
            revealed: true,
        };
    });
};
const definitionMeterLevels = (rulebook, viewMeter) => {
    const meter = rulebook.meters.find((candidate) => candidate.id === viewMeter.id);
    if (meter === undefined || !("levels" in meter)) {
        return undefined;
    }
    return meter.levels;
};
/** Cover/content NSFW flags shared by owner and public profile mapping. */
const nsfwFields = (entity) => ({
    ...(entity.cover_is_nsfw !== undefined || entity.is_nsfw !== undefined ? { isCoverNsfw: entity.cover_is_nsfw ?? entity.is_nsfw } : {}),
    ...(entity.is_nsfw !== undefined && { isNsfw: entity.is_nsfw }),
});
/** The owner's full definition — reconstructs the embedded document from the server row. */
const ownerDefinition = (rulebook) => ({
    id: rulebook.id,
    name: rulebook.name,
    ...(rulebook.description !== undefined && { description: rulebook.description }),
    meters: rulebook.meters,
    combined_rules: rulebook.combined_rules,
});
const revealAllMeters = (rulebook, meters) => meters.map((viewMeter) => {
    const meter = rulebook.meters.find((candidate) => candidate.id === viewMeter.id);
    const clock = meter?.type === MeterType.CLOCK ? meter : undefined;
    return {
        ...viewMeter,
        levels: revealAllLevels(definitionMeterLevels(rulebook, viewMeter), viewMeter.levels) ?? [],
        ...(viewMeter.day_levels !== undefined ? { day_levels: revealAllLevels(clock?.day_levels, viewMeter.day_levels) } : {}),
        ...(viewMeter.tod_levels !== undefined ? { tod_levels: revealAllLevels(clock?.tod_levels, viewMeter.tod_levels) } : {}),
    };
});
const fromOwnerView = (rulebook) => {
    const view = toRulebookPublicView(rulebook);
    return {
        id: rulebook.id,
        name: rulebook.name,
        ...(rulebook.description !== undefined && { description: rulebook.description }),
        tags: rulebook.tags,
        ...(rulebook.cover_url !== undefined && { coverUrl: rulebook.cover_url }),
        ...nsfwFields(rulebook),
        ...(rulebook.creator_username !== undefined && { creatorUsername: rulebook.creator_username }),
        creatorUserId: rulebook.creator_user_id,
        meters: revealAllMeters(rulebook, view.meters),
        hasHiddenMeters: view.has_hidden_meters ?? false,
        numMeters: rulebook.num_meters ?? rulebook.meters.length,
        numRules: rulebook.num_rules ?? countRulebookRules(rulebook),
        numHiddenMeters: rulebook.num_hidden_meters ?? countHiddenMeters(rulebook.meters),
        numAttachedCharacters: rulebook.num_attached_characters ?? 0,
        numAttachedPersonas: rulebook.num_attached_personas ?? 0,
        definitionVisible: rulebook.definition_visible,
        isOwner: true,
        definition: ownerDefinition(rulebook),
        visibility: rulebook.visibility,
        status: rulebook.status,
        ...(rulebook.moderation_status !== undefined && { moderationStatus: rulebook.moderation_status }),
        ...(rulebook.moderation_report_status !== undefined && { moderationReportStatus: rulebook.moderation_report_status }),
    };
};
const fromPublicProfile = (view) => ({
    id: view.id,
    name: view.name,
    ...(view.description !== undefined && { description: view.description }),
    tags: view.tags,
    ...(view.cover_url !== undefined && { coverUrl: view.cover_url }),
    ...nsfwFields(view),
    ...(view.creator_username !== undefined && { creatorUsername: view.creator_username }),
    creatorUserId: view.creator_user_id,
    meters: view.meters,
    hasHiddenMeters: view.has_hidden_meters ?? false,
    numMeters: view.num_meters,
    numRules: view.num_rules ?? (view.definition ? countRulebookRules(view.definition) : 0),
    numHiddenMeters: view.num_hidden_meters ?? (view.definition ? countHiddenMeters(view.definition.meters) : 0),
    numAttachedCharacters: view.num_attached_characters,
    numAttachedPersonas: view.num_attached_personas,
    definitionVisible: view.definition_visible,
    isOwner: false,
    ...(view.definition !== undefined && { definition: view.definition }),
});
/**
 * `GET /rulebooks/{id}` for the public profile page. The endpoint answers an owner view or a
 * public profile depending on the caller; owner rows carry `visibility`, public profiles don't —
 * that one field distinguishes the two shapes. `null` on 404.
 */
export async function getRulebookProfile(rulebookId) {
    try {
        const { data: response } = await axiosClient.get(rulebooksUrl(`/${encodeURIComponent(rulebookId)}`));
        return "visibility" in response ? fromOwnerView(response) : fromPublicProfile(response);
    }
    catch (error) {
        if (error?.response?.status === HttpStatusCode.NOT_FOUND) {
            return null;
        }
        throw error;
    }
}
